import { useAppDispatch, useAppSelector } from "@/app/store/store"
import axios from "axios"
import { append, IsLoading, merge, pop, reset, setLoading } from "./slice"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Operator } from "@/savaradio/common"
import { useStoreRealtime } from "../../../realtime/hook"
import { useStoreSession } from "../../../session/hook"

const url = process.env.NEXT_PUBLIC_URL_BACK

export function useStoreOperator(init_data?: boolean, init_socket?: boolean) {
  const dispatch = useAppDispatch()
  const operators = useAppSelector(state => state.operator_slice.value)
  const isLoading = useAppSelector(state => state.operator_slice.isLoading)
  const { connect, status, emit, signin } = useStoreRealtime()
  const operator_status = status("operator")
  const { session } = useStoreSession()
  const [progress, setProgress] = useState<number>(0)
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  const setIsLoading = useCallback((p: Partial<IsLoading>) => {
    dispatch(setLoading(p))
  }, [dispatch])

  const get_operator = useCallback(async () => {
    if (isLoading.get) return
    setIsLoading({ get: true })
    try {
      const { data } = await axios.get("/api/crud/operator", { responseType: "json" })
      dispatch(reset(data || []))
    } catch {
      dispatch(reset([]))
    } finally {
      setIsLoading({ get: false })
    }
  }, [dispatch, setIsLoading, isLoading.get])

  const create = useCallback(async (operator: Omit<Operator, "_id" | "token">) => {
    setIsLoading({ create: true })
    try {
      const { data } = await axios.post("/api/crud/operator", operator, { responseType: "json" })
      if (data) {
        axios.post("/api/auth/email/usercreation", {
          id: data._id,
          email: operator.email,
          name: operator.username,
          type: "operator",
          link: `${url}/auth/pass` || "http://localhost:3000/auth/pass"
        })
        dispatch(append(data))
        if (operator_status === "signed") emit("operator", "create", data)
        return data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ create: false })
    }
  }, [dispatch, emit, operator_status, setIsLoading])

  const remove = useCallback(async (id: string) => {
    setIsLoading({ delete: true })
    try {
      const { data } = await axios.delete(`/api/crud/operator/${id}`, { responseType: "json" })
      if (data) {
        dispatch(pop(data))
        if (operator_status === "signed") emit("operator", "delete", data)
        return data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ delete: false })
    }
  }, [dispatch, emit, operator_status, setIsLoading])

  const update = useCallback(async (id: string, upd: Partial<Operator>) => {
    setIsLoading({ update: true })
    try {
      const { data } = await axios.patch(`/api/crud/operator/${id}`, upd, { responseType: "json" })
      if (data) {
        const new_data = { ...data, ...upd }
        dispatch(merge(data))
        if (operator_status === "signed") emit("operator", "update", new_data)
        return new_data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ update: false })
    }
  }, [dispatch, emit, operator_status, setIsLoading])

  const upload = useCallback(async (file: File) => {
    setIsLoading({ upload: true })
    if (file.size === 0) return null;
    try {
      const abort = new AbortController()
      const { data } = await axios.get(
        `/api/minio?type=${encodeURIComponent(file.type)}&name=${encodeURIComponent(file.name)}&folder=${encodeURIComponent("images/avatar/operator")}`,
        { signal: abort.signal }
      );

      if (data?.signedUrl && data?.path) {
        try {
          const { status } = await axios.put(data.signedUrl, file, {
            signal: abort.signal,
            onUploadProgress: (e) => {
              if (e.total) {
                const percentCompleted = Math.round((e.loaded * 100) / e.total);
                setProgress(percentCompleted);
              }
            }
          });
          return status === 200 || status === 201 ? data.path : null
        }
        catch (err) {
          return null
        }
      }
      return null;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Upload annulé :", file.name);
        setAbortController(null)
      }
      return null;
    } finally {
      setIsLoading({ upload: false })
      setAbortController(null)
    }
  }, [progress, abortController]);

  const cancelRequest = useCallback(() => {
    abortController?.abort()
  }, [abortController])

  const find_sync = useCallback((id: string) => {
    return operators?.find(operator => operator._id === id)
  }, [operators])

  const initializing = useMemo(() => {
    return isLoading.get && (!operators || operators.length === 0)
  }, [isLoading.get, operators])

  useEffect(() => {
    if (init_data && !operators && !isLoading.get) {
      get_operator()
    }
  }, [init_data, operators, get_operator, isLoading.get])

  useEffect(() => {
    if (init_socket && operator_status === "disconnected") {
      connect("operator")
    }
  }, [init_socket, operator_status, connect])

  useEffect(() => {
    if (operator_status === "connected" && session) {
      signin("operator", session)
    }
  }, [operator_status, session, signin])

  return useMemo(() => ({
    operators,
    isLoading,
    initializing,
    progress,
    cancelRequest,
    crud: { create, remove, update, upload, get: get_operator },
    refresh: get_operator,
    find: { sync: find_sync }
  }), [operators, isLoading, initializing, progress, create, remove, update, upload, get_operator, find_sync, cancelRequest])
}